/**
 * governance/utils/languageQuality.ts
 * Language quality checks: jargon detection, passive voice, sentence length.
 * Source: CONTENT_GOVERNANCE_FRAMEWORK.md §1.1, §1.2, §4.1
 */

// ─── Jargon Detection ─────────────────────────────────────────────────────────

/**
 * Forbidden jargon patterns for explanation and distractor text.
 * These terms are too complex for children aged 9-11 (groep 6-8).
 * Source: CONTENT_GOVERNANCE_FRAMEWORK.md §1.1 + Bijlage B
 */
export const JARGON_PATTERNS: Array<{ pattern: RegExp; replacement: string }> =
  [
    { pattern: /\bstrategie\b/i, replacement: '"handige tip"' },
    { pattern: /\balgoritme\b/i, replacement: '"stappenplan"' },
    {
      pattern: /\bcommutatief\b/i,
      replacement: '"je mag de volgorde omdraaien"',
    },
    {
      pattern: /\bassociatief\b/i,
      replacement: '"je mag de haakjes verplaatsen"',
    },
    { pattern: /\bproportioneel\b/i, replacement: '"in verhouding"' },
    { pattern: /\bdistributief\b/i, replacement: '(avoid)' },
    { pattern: /\bexponentieel\b/i, replacement: '(avoid)' },
    { pattern: /\bnoemer\b/i, replacement: '"getal onder de streep"' },
    { pattern: /\bteller\b/i, replacement: '"getal boven de streep"' },
    { pattern: /\bdecimaalgetal\b/i, replacement: '"kommagetal"' },
  ]

/**
 * Passive voice patterns forbidden in child-facing explanations.
 * Source: CONTENT_GOVERNANCE_FRAMEWORK.md §4.1
 */
export const PASSIVE_VOICE_PATTERNS: RegExp[] = [
  /\ber wordt\b/i,
  /\ber worden\b/i,
  /\bhet wordt berekend\b/i,
  /\bmen kan\b/i,
  /\bwordt berekend\b/i,
  /\bwordt gevonden\b/i,
  /\bwordt bepaald\b/i,
  /\bwordt uitgerekend\b/i,
  /\bkan worden\b/i,
]

// ─── Sentence Length ──────────────────────────────────────────────────────────

/**
 * Returns the maximum words per sentence for a given tier.
 *
 * Phase 4: this used to hardcode 18/22/25. The threshold is now sourced from
 * `defaultPolicy.textQuality.maxSentenceLengthByTierBand` and resolved via
 * `policyEngine.getMaxSentenceLength(tier)`, so admin overrides take effect.
 * The hardcoded fallback below is kept ONLY for the (impossible-in-practice)
 * case where the policy engine has not yet been initialised — it mirrors the
 * historical values exactly so behaviour is value-preserving.
 *
 * Source: CONTENT_GOVERNANCE_FRAMEWORK.md §1.2, §4.1
 */
export function getMaxWordsPerSentence(tier: number): number {
  // Delegate to the policy engine. We intentionally late-require to avoid a
  // hard import cycle (utils → pipeline → utils) at module-init time; the
  // function below is only called during pipeline runs, well after init.
  try {
    // eslint-disable-next-line @typescript-eslint/no-require-imports
    const policyEngine = require('../pipeline/policyEngine') as {
      getMaxSentenceLength?: (t: number) => number
    }
    if (typeof policyEngine.getMaxSentenceLength === 'function') {
      return policyEngine.getMaxSentenceLength(tier)
    }
  } catch {
    // fall through to historical default
  }
  if (tier <= 4) return 18
  if (tier <= 8) return 22
  return 25
}

/**
 * Returns sentences that exceed the tier-appropriate word limit.
 * Returns empty array if all sentences are within limits.
 */
export function detectLongSentences(text: string, tier: number): string[] {
  const maxWords = getMaxWordsPerSentence(tier)
  const sentences = text
    .split(/[.!?]+/)
    .map((s) => s.trim())
    .filter((s) => s.length > 0)
  return sentences.filter((s) => s.split(/\s+/).length > maxWords)
}

// ─── Jargon Detection Function ────────────────────────────────────────────────

/**
 * Returns matched jargon terms found in the text.
 * Returns empty array if no jargon detected.
 *
 * ENHANCED: Accepts optional tier for tier-aware filtering.
 * "teller" and "noemer" are standard curriculum vocabulary for advanced+ tiers (9+)
 * and should only be flagged for basic band (tiers 1-4).
 */
export function detectJargon(
  text: string,
  tier?: number,
): Array<{ term: string; replacement: string }> {
  const found: Array<{ term: string; replacement: string }> = []
  for (const { pattern, replacement } of JARGON_PATTERNS) {
    const match = text.match(pattern)
    if (match) {
      if (tier !== undefined && tier > 4) {
        const termLower = match[0].toLowerCase()
        if (termLower === 'teller' || termLower === 'noemer') {
          continue
        }
      }
      found.push({ term: match[0], replacement })
    }
  }
  return found
}

/**
 * Returns true if the text contains passive voice constructions.
 */
export function hasPassiveVoice(text: string): boolean {
  return PASSIVE_VOICE_PATTERNS.some((p) => p.test(text))
}
